import type { AuthUser, UserRole } from '@/types';

export const USER_KEY = 'sr_user';
export const IMPERSONATION_ORIGINAL_KEY = 'sr_impersonation_original';
export const AUTH_SESSION_CHANGED_EVENT = 'sr-auth-session-changed';

export interface JwtPayload {
  sub: string;
  role: string;
  tenant_id: string | null;
  exp: number;
  impersonator_user_id?: string;
}

export interface ImpersonationOriginal {
  /** Originaler Access-Token des Super-Admins (vor Impersonation). */
  access: string;
  /** Benutzerobjekt des Super-Admins. */
  user: AuthUser;
  /** Impersonation-Access-Token (für Rehydration nach Page-Reload). */
  impersonatedAccess: string;
  /** Benutzerobjekt des impersonierten Nutzers (Acting-As). */
  impersonatedUser: AuthUser;
}

/**
 * Gibt zurück, ob der Impersonation-Access-Token noch nicht abgelaufen ist.
 * Wird genutzt, um nach Page-Reload zu entscheiden, ob die Impersonation
 * wiederhergestellt werden kann.
 */
export function isImpersonationValid(original: ImpersonationOriginal): boolean {
  const payload = parseJwt(original.impersonatedAccess);
  if (!payload) return false;
  return payload.exp * 1000 > Date.now();
}

export interface SessionSyncResult {
  payload: JwtPayload;
  user: AuthUser;
  impersonationCleared: boolean;
}

export function parseJwt(token: string): JwtPayload | null {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(jsonPayload) as JwtPayload;
  } catch {
    return null;
  }
}

export function storeUser(user: AuthUser): void {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  notifyAuthSessionChanged();
}

export function loadUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

export function clearUser(): void {
  localStorage.removeItem(USER_KEY);
  notifyAuthSessionChanged();
}

export const impersonationStorage = {
  begin(original: ImpersonationOriginal): void {
    sessionStorage.setItem(IMPERSONATION_ORIGINAL_KEY, JSON.stringify(original));
    notifyAuthSessionChanged();
  },
  peek(): ImpersonationOriginal | null {
    const raw = sessionStorage.getItem(IMPERSONATION_ORIGINAL_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as ImpersonationOriginal;
    } catch {
      return null;
    }
  },
  end(): ImpersonationOriginal | null {
    const original = impersonationStorage.peek();
    sessionStorage.removeItem(IMPERSONATION_ORIGINAL_KEY);
    notifyAuthSessionChanged();
    return original;
  },
  clear(): void {
    sessionStorage.removeItem(IMPERSONATION_ORIGINAL_KEY);
    notifyAuthSessionChanged();
  },
  isActive(): boolean {
    return !!sessionStorage.getItem(IMPERSONATION_ORIGINAL_KEY);
  },
};

export function isSuperAdmin(): boolean {
  return loadUser()?.role === 'super_admin';
}

export function isPortalPath(pathname: string): boolean {
  return pathname === '/dashboard' || pathname.startsWith('/dashboard/');
}

export function shouldRedirectSuperAdminFromPortal(pathname: string): boolean {
  return isSuperAdmin() && isPortalPath(pathname);
}

export function syncSessionFromAccessToken(token: string): SessionSyncResult | null {
  const payload = parseJwt(token);
  if (!payload) return null;

  const currentUser = loadUser();
  const original = impersonationStorage.peek();
  const fallbackUser =
    currentUser?.id === payload.sub
      ? currentUser
      : original?.user.id === payload.sub
        ? original.user
        : null;

  const user: AuthUser = {
    id: payload.sub,
    email: fallbackUser?.email ?? '',
    role: payload.role as UserRole,
    tenantId: payload.tenant_id,
    requires2fa: fallbackUser?.requires2fa ?? false,
  };

  storeUser(user);

  const impersonationCleared = !payload.impersonator_user_id && impersonationStorage.isActive();
  if (impersonationCleared) {
    impersonationStorage.clear();
  }

  return { payload, user, impersonationCleared };
}

function notifyAuthSessionChanged(): void {
  window.dispatchEvent(new Event(AUTH_SESSION_CHANGED_EVENT));
}
